import { useEffect, useState, useMemo } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { CircularProgress } from "@mui/material";
import api from "../../../../api/api";
import LineChart from "../../../../graphs/linegraphs/trendslinegraph";
import { BloodPressure, WeightIcon, PulseIcon } from "../vitals/svgs";
import { buildRpmChartLabels, buildRpmHistoryRows, rpmAxisLabel } from "./rpmdata";
import { getPracticeId } from "../../../../config/practice";

const RPM = ({ dataId }) => {
  const [graphSwitch, setGraphSwitch] = useState({
    Systolic: true,
    Diastolic: true,
    Pulse: true,
    Weight: true,
    BMI: true,
    O2Saturation: true,
  });

  const [subTabValue, setSubTabValue] = useState(0); // 0: BP, 1: Pulse, 2: Weight
  const [selectedMonth, setSelectedMonth] = useState('Yearly');

  const [rpmData, setRpmData] = useState({});
  const [wlmData, setWlmData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const practiseid = getPracticeId();
  const PatientIDRPM = dataId.patientid;

  const fetchrpmData = async () => {
    try {
      const response = await api.post(`/v1/${practiseid}/patient/${PatientIDRPM}/rpm/bpp-master`, {});
      if (response.status === 200) {
        setRpmData(response.data);
      }
    } catch (error) {
      console.error("Error fetching rpm data:", error.response ? error.response.data : error);
    }
  };

  const fetchWlmData = async () => {
    try {
      const response = await api.post(`/v1/${practiseid}/patient/${PatientIDRPM}/rpm/wlm-master`, {});
      if (response.status === 200) {
        setWlmData(response.data);
      }
    } catch (error) {
      console.error("Error fetching WLM data:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await Promise.all([fetchrpmData(), fetchWlmData()]);
      setIsLoading(false);
    };
    fetchData();
  }, []);

  // Chart labels: one unique "MM-DD-YYYY HH:mm:ss" label per reading (see
  // rpmdata.js); the axis still shows the datelabel text via rpmAxisLabel.
  const combinedVitalsData = useMemo(() => {
    const bpLabels = buildRpmChartLabels(rpmData.datetime, rpmData.datelabel);
    // wlm-master's `datetime` is the row's insert time, so weight uses datelabel.
    const wtLabels = buildRpmChartLabels([], wlmData.datelabel);
    return {
      systolicdatelabel: bpLabels,
      diastolicdatelabel: bpLabels,
      pulsedatelabel: bpLabels,
      systolic: rpmData.systolic || [],
      diastolic: rpmData.diastolic || [],
      pulse: rpmData.pulse || [],

      wtdatelabel: wtLabels,
      bmidatelabel: wtLabels,
      weight: wlmData.weight || [],
      bmi: wlmData.bmi || []
    };
  }, [rpmData, wlmData]);

  const historyRows = useMemo(
    () => buildRpmHistoryRows(subTabValue, rpmData, wlmData),
    [subTabValue, rpmData, wlmData]
  );
  const weightUnit = (wlmData.weightunits && wlmData.weightunits[0]) || "lbs";

  const toggleGraph = (type) => {
    setGraphSwitch((prevState) => ({
      ...prevState,
      [type]: !prevState[type],
    }));
  };

  const handleSubTabChange = (event, newValue) => {
    setSubTabValue(newValue);
  };

  return (
    <div className="flex flex-col h-auto w-[98%] max-h-[70vh] p-3 pr-6 pl-6 mb-16 bg-[white] rounded-xl overflow-y-auto custom-scrollbar">
      
      {/* Main Header & Time Period filter */}
      <div className="flex justify-between w-full items-center">
        <div>
          <div className="w-full mt-1 font-semibold text-[#3a405b] text-xl tracking-[1.00px] leading-[17px] whitespace-nowrap">   
            RPM
          </div>
          <div className="mt-2 text-sm text-[#7F8B9B]">Remote Patient Monitoring Data</div>
        </div>
        
        <div className="flex flex-col items-start w-28">
          <div className="relative w-full">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="appearance-none bg-white border-[1px] border-[#DAE3F8] rounded-lg px-3 py-2 pr-8 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#E17954] focus:border-transparent cursor-pointer w-full"
            >
              <option value="Yearly">Yearly</option>
              <option value="Weekly">Weekly</option>
              <option value="Monthly">Monthly</option>
              <option value="3 Months">3 Months</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg className="w-4 h-4 text-[#3788E5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="w-[100%] flex justify-center mt-10 items-center">
          <CircularProgress color="secondary" />
        </div>
      ) : (
        <div className="mt-4">
          {/* Sub-Tabs exactly like Vitals */}
          <Tabs
            value={subTabValue}
            onChange={handleSubTabChange}
            style={{
              background: "transparent",
              border: "1px solid #DAE3F8",
              borderRadius: "15px",
              display: "flex",
            }}
            variant="fullWidth"
            indicatorColor="none"
          >
            {/* BP */}
            <Tab
              label={
                <div className="flex items-center gap-2">
                  {BloodPressure}
                  <span>BP</span>
                </div>
              }
              sx={{
                backgroundColor: subTabValue === 0 ? "#E17954" : "transparent",
                color: subTabValue === 0 ? "#fff" : "#000",
                flexGrow: 1,
                borderRadius: subTabValue === 0 ? "12px" : "0px",
                paddingX: "1px",
                paddingY: "6px",
                fontSize: "14px",
                fontWeight: "medium",
                minHeight: "32px",
                "&.Mui-selected": { color: "#fff" },
                margin: "5px",
                textTransform: "none",
              }}
            />
            {/* PULSE */}
            <Tab
              label={
                <div className="flex items-center gap-2">
                  {PulseIcon}
                  <span>PULSE</span>
                </div>
              }
              sx={{
                backgroundColor: subTabValue === 1 ? "#E17954" : "transparent",
                color: subTabValue === 1 ? "#fff" : "#000",
                flexGrow: 1,
                borderRadius: subTabValue === 1 ? "12px" : "0px",
                paddingX: "1px",
                paddingY: "6px",
                fontSize: "14px",
                minHeight: "32px",
                "&.Mui-selected": { color: "#fff" },
                margin: "5px",
                textTransform: "none",
              }}
            />
            {/* WEIGHT */}
            <Tab
              label={
                <div className="flex items-center gap-2">
                  {WeightIcon}
                  <span>WEIGHT</span>
                </div>
              }
              sx={{
                backgroundColor: subTabValue === 2 ? "#E17954" : "transparent",
                color: subTabValue === 2 ? "#fff" : "#000",
                flexGrow: 1,
                borderRadius: subTabValue === 2 ? "12px" : "0px",
                paddingX: "1px",
                paddingY: "6px",
                fontSize: "14px",
                minHeight: "32px",
                "&.Mui-selected": { color: "#fff" },
                margin: "5px",
                textTransform: "none",
              }}
            />
          </Tabs>

          {/* Graph Component */}
          <div
            className="mt-2 w-full"
            style={{ height: "270px", width: "100%", display: "flex" }}
          >
            <LineChart
              resdata={combinedVitalsData}
              graphSwitch={graphSwitch}
              selectedTab={subTabValue}
              selectedMonth={selectedMonth}
              xLabelFormatter={rpmAxisLabel}
            />
          </div>

          {/* Graph Toggles */}
          <div className="flex justify-around items-center mt-5">
            <div className="flex gap-5">
              {subTabValue === 0 && (
                <>
                  <div
                    onClick={() => toggleGraph("Systolic")}
                    className={`text-center font-normal text-base leading-5 cursor-pointer relative ${graphSwitch.Systolic ? "text-[#E17954]" : "text-gray-700"}`}
                  >
                    Systolic
                    {graphSwitch.Systolic && (
                      <div className="absolute bottom-[-4px] left-0 right-0 h-[3px] rounded-[2px] bg-[#E17954]" />
                    )}
                  </div>
                  <div
                    onClick={() => toggleGraph("Diastolic")}
                    className={`text-center font-normal text-base leading-5 cursor-pointer relative ${graphSwitch.Diastolic ? "text-[#1D2951]" : "text-gray-700"}`}
                  >
                    Diastolic
                    {graphSwitch.Diastolic && (
                      <div className="absolute bottom-[-4px] left-0 right-0 h-[3px] rounded-[2px] bg-[#1D2951]" />
                    )}
                  </div>
                </>
              )}
              {subTabValue === 1 && (
                <div
                  onClick={() => toggleGraph("Pulse")}
                  className={`text-center font-normal text-base leading-5 cursor-pointer relative ${graphSwitch.Pulse ? "text-[#E17954]" : "text-gray-700"}`}
                >
                  Pulse
                  {graphSwitch.Pulse && (
                    <div className="absolute bottom-[-4px] left-0 right-0 h-[3px] rounded-[2px] bg-[#E17954]" />
                  )}
                </div>
              )}
              {subTabValue === 2 && (
                <>
                  <div
                    onClick={() => toggleGraph("Weight")}
                    className={`text-center font-normal text-base leading-5 cursor-pointer relative ${graphSwitch.Weight ? "text-[#E17954]" : "text-gray-700"}`}
                  >
                    Weight
                    {graphSwitch.Weight && (
                      <div className="absolute bottom-[-4px] left-0 right-0 h-[3px] rounded-[2px] bg-[#E17954]" />
                    )}
                  </div>
                  <div
                    onClick={() => toggleGraph("BMI")}
                    className={`text-center font-normal text-base leading-5 cursor-pointer relative ${graphSwitch.BMI ? "text-[#1D2951]" : "text-gray-700"}`}
                  >
                    BMI
                    {graphSwitch.BMI && (
                      <div className="absolute bottom-[-4px] left-0 right-0 h-[3px] rounded-[2px] bg-[#1D2951]" />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* History Table below graph */}
          <div className="mt-8 border-t border-[#DAE3F8] pt-4">
            <h3 className="text-lg font-semibold text-[#1D2951] mb-4">All Readings History</h3>
            <div className="overflow-x-auto max-h-[300px] overflow-y-auto custom-scrollbar">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-[#1D2951] uppercase bg-[#FFF3EF] sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 border-b border-[#DAE3F8] rounded-tl-lg">Date</th>
                    <th className="px-4 py-3 border-b border-[#DAE3F8]">
                      {subTabValue === 0 ? "Systolic (mmHg)" : subTabValue === 1 ? "Pulse (bpm)" : `Weight (${weightUnit})`}
                    </th>
                    {subTabValue === 0 && <th className="px-4 py-3 border-b border-[#DAE3F8] rounded-tr-lg">Diastolic (mmHg)</th>}
                    {subTabValue === 2 && <th className="px-4 py-3 border-b border-[#DAE3F8] rounded-tr-lg">BMI</th>}
                  </tr>
                </thead>
                <tbody>
                  {historyRows.length === 0 ? (
                    <tr><td colSpan="3" className="px-4 py-4 text-center text-gray-500">No historical data available.</td></tr>
                  ) : (
                    historyRows.map((r, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-[#FAFBFC] transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900">{r.date || '--'}</td>
                        <td className="px-4 py-3">
                          <span className={subTabValue === 2 ? "font-semibold" : "font-semibold text-[#1D2951]"}>
                            {subTabValue === 2 ? (r.val1 ? `${r.val1} ${r.unit}` : '--') : (r.val1 || '--')}
                          </span>
                        </td>
                        {(subTabValue === 0 || subTabValue === 2) && (
                          <td className="px-4 py-3 font-semibold text-gray-700">{r.val2 || '--'}</td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RPM;
